/**
 * purchaseReturns.ts — Pure aggregation of goods returned to suppliers: the
 * mirror of the Purchase Register, and a quality signal (what is being sent back
 * and why). Rolls returns up per supplier and per reason.
 *
 * Pure — the screen feeds it rows from the data source.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface PurchaseReturnInput {
  returnNumber: string;
  date: Date;
  supplierName: string;
  reason: string | null;
  status: string;
  /** Return total (header `total_amount`). */
  value: number;
  /** Total units returned across the return's items. */
  quantity: number;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ReturnRow extends PurchaseReturnInput {
  reasonLabel: string;
}

export interface SupplierReturnSummary {
  supplierName: string;
  count: number;
  value: number;
  quantity: number;
}

export interface ReasonSummary {
  reason: string;
  count: number;
  value: number;
}

export interface PurchaseReturns {
  /** Returns, newest first. */
  rows: ReturnRow[];
  totals: { count: number; value: number; quantity: number };
  bySupplier: SupplierReturnSummary[];
  byReason: ReasonSummary[];
}

const UNSPECIFIED = 'Unspecified';

function reasonLabel(reason: string | null): string {
  const r = reason?.trim();
  return r ? r : UNSPECIFIED;
}

export function buildPurchaseReturns(returns: PurchaseReturnInput[]): PurchaseReturns {
  const rows: ReturnRow[] = returns
    .map((r) => ({ ...r, reasonLabel: reasonLabel(r.reason) }))
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  const totals = { count: rows.length, value: 0, quantity: 0 };
  const supMap = new Map<string, { count: number; value: number; quantity: number }>();
  const reasonMap = new Map<string, { count: number; value: number }>();

  for (const r of rows) {
    totals.value += r.value;
    totals.quantity += r.quantity;

    const s = supMap.get(r.supplierName) ?? { count: 0, value: 0, quantity: 0 };
    s.count += 1;
    s.value += r.value;
    s.quantity += r.quantity;
    supMap.set(r.supplierName, s);

    const rs = reasonMap.get(r.reasonLabel) ?? { count: 0, value: 0 };
    rs.count += 1;
    rs.value += r.value;
    reasonMap.set(r.reasonLabel, rs);
  }

  const bySupplier: SupplierReturnSummary[] = [...supMap.entries()]
    .map(([supplierName, v]) => ({ supplierName, count: v.count, value: round2(v.value), quantity: round2(v.quantity) }))
    .sort((a, b) => b.value - a.value);

  const byReason: ReasonSummary[] = [...reasonMap.entries()]
    .map(([reason, v]) => ({ reason, count: v.count, value: round2(v.value) }))
    .sort((a, b) => b.value - a.value);

  return {
    rows,
    totals: { count: totals.count, value: round2(totals.value), quantity: round2(totals.quantity) },
    bySupplier,
    byReason,
  };
}
